import { createSlice, isAnyOf } from "@reduxjs/toolkit";

import { TaskAxios } from "../../axios/task/task.axios";
import { PageDto } from "../../model/meta/PageDto";
import { PageResponseDto } from "../../model/meta/PageResponseDto";
import { AssignTaskDto } from "../../model/shared/dto/AssignTaskDto";

import { GenericGetTasksDto } from "../../model/task/dto/GenericGetTasksDto";
import { GetTasksSettingsDto } from "../../model/task/dto/GetTasksSettingsDto";
import { TasksSettingsPatchDto } from "../../model/task/dto/TasksSettingsPatchDto";

import { AtiraThunk } from "../AtiraThunk";
import { BaseTask } from "../../model/task/Task";
import { TasksSettings } from "../../model/task/TaskSettings";
import { CreateTaskDto } from "../../model/task/dto/CreateTaskDto";
import { UpdateTaskDto } from "../../model/task/dto/UpdateTaskDto";
import { DeleteTaskDto } from "../../model/task/dto/DeleteTaskDto";
import { IncrementTaskContactAttemptsDto } from "../../model/task/dto/IncrementTaskContactAttemptsDto";
import { GetTaskDto } from "../../model/task/dto/GetTaskDto";
import { revertAllReduxAction } from "../store-reset";
import { userActions } from "../user/user.slice";

interface TaskReducer {
  //
  tasksSettings: TasksSettings | null;
  allTasksLoading: boolean;
  allTasks: BaseTask[];
  allTasksMeta: PageDto | null;
  todayTasks: BaseTask[];
  todayTasksLoading: boolean;
  tasksPage: number;
  tasksPageSize: number;
  //
  allTasksPendingRequestId?: string;
}

const initialState = Object.freeze<TaskReducer>({
  tasksSettings: null,
  allTasksLoading: false,
  allTasks: [],
  allTasksMeta: null,
  todayTasks: [],
  todayTasksLoading: false,
  tasksPage: 1,
  tasksPageSize: 10,
  allTasksPendingRequestId: undefined,
});

const getTasksSettings = AtiraThunk<TasksSettings, GetTasksSettingsDto>(
  "/tasks/settings",
  TaskAxios.getTasksSettings,
);

const updateTasksSettings = AtiraThunk<void, TasksSettingsPatchDto>(
  "/tasks/settings/patch",
  TaskAxios.updateTasksSettings,
);

const getAllTasks = AtiraThunk<PageResponseDto<BaseTask[]>, GenericGetTasksDto>(
  `/task/all`,
  TaskAxios.getAllTasks,
);

const getTodayTasks = AtiraThunk<
  PageResponseDto<BaseTask[]>,
  GenericGetTasksDto
>(`/task/all/today`, TaskAxios.getAllTasks);

const createTask = AtiraThunk<void, CreateTaskDto>(
  "task/create",
  TaskAxios.createTask,
);

const updateTask = AtiraThunk<void, UpdateTaskDto>(
  "task/update",
  TaskAxios.updateTask,
);

const deleteTask = AtiraThunk<void, DeleteTaskDto>(
  "task/delete",
  TaskAxios.deleteTask,
);

const assignTask = AtiraThunk<void, AssignTaskDto>(
  "task/assign",
  TaskAxios.assignTask,
);

const incrementTaskContactAttempts = AtiraThunk<
  void,
  IncrementTaskContactAttemptsDto
>(`/task/increment-attempt`, TaskAxios.IncrementTaskContactAttempts);

const getTask = AtiraThunk<BaseTask, GetTaskDto>(
  `/task/get`,
  TaskAxios.getTask,
);

const taskSlice = createSlice({
  name: "task",
  initialState,
  reducers: {
    setTasksTablePageSize: (state, action) => {
      state.tasksPageSize = action.payload;
    },

    setTasksTablePage: (state, action) => {
      state.tasksPage = action.payload;
    },
  },
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(getTasksSettings.fulfilled, (state, action) => {
      state.tasksSettings = action.payload;
    });

    addCase(getAllTasks.pending, (state, action) => {
      state.allTasksPendingRequestId = action.meta.requestId;
      state.allTasksLoading = true;
    });
    addCase(getAllTasks.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.allTasksPendingRequestId) {
        return;
      }

      state.allTasks = action.payload.data;
      state.allTasksMeta = action.payload.meta;
      state.allTasksLoading = false;
      state.allTasksPendingRequestId = undefined;
    });
    addCase(getAllTasks.rejected, (state, action) => {
      if (action.meta.requestId !== state.allTasksPendingRequestId) {
        return;
      }

      state.allTasksLoading = false;
      state.allTasksPendingRequestId = undefined;
    });

    addCase(getTask.fulfilled, (state, action) => {
      state.allTasks = state.allTasks.map((task) =>
        task._id === action.payload._id ? action.payload : task,
      );
    });

    addCase(getTodayTasks.pending, (state) => {
      state.todayTasksLoading = true;
    });
    addCase(getTodayTasks.fulfilled, (state, action) => {
      state.todayTasks = action.payload.data;
      state.todayTasksLoading = false;
    });
    addCase(getTodayTasks.rejected, (state) => {
      state.todayTasksLoading = false;
    });

    addMatcher(isAnyOf(userActions.logout, revertAllReduxAction), (state) => {
      return (state = initialState);
    });
  },
});

export const taskActions = {
  ...taskSlice.actions,
  getTasksSettings,
  updateTasksSettings,
  getAllTasks,
  getTodayTasks,
  createTask,
  updateTask,
  deleteTask,
  assignTask,
  getTask,
  incrementTaskContactAttempts,
};

export default taskSlice.reducer;
